// usePortfolioData.js — Data hook for portfolio analytics
// Fetches from API with mock data fallback (demo mode)

import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import {
  PORTFOLIO_POSITIONS,
  PORTFOLIO_PERFORMANCE,
  BENCHMARK_RETURNS,
  FX_HEDGES,
  CASH_FLOW_SCHEDULE,
  RISK_LIMITS,
  STRESS_SCENARIOS,
  IPS_COMPLIANCE,
  MANDATE_LIMITS,
  COMMITTEE_SCHEDULE,
  AUDIT_TRAIL,
  REGULATORY_REPORTS,
  SIGNATORY_AUTHORITY,
} from '../data/portfolioMockData';

const usePortfolioData = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    positions: PORTFOLIO_POSITIONS,
    performance: PORTFOLIO_PERFORMANCE,
    benchmarks: BENCHMARK_RETURNS,
    fxHedges: FX_HEDGES,
    cashFlows: CASH_FLOW_SCHEDULE,
    riskLimits: RISK_LIMITS,
    stressScenarios: STRESS_SCENARIOS,
    governance: {
      ipsCompliance: IPS_COMPLIANCE,
      mandateLimits: MANDATE_LIMITS,
      committeeSchedule: COMMITTEE_SCHEDULE,
      auditTrail: AUDIT_TRAIL,
      regulatoryReports: REGULATORY_REPORTS,
      signatoryAuthority: SIGNATORY_AUTHORITY,
    },
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [
        positions,
        performance,
        cashFlows,
        risk,
        fxHedges,
        benchmarks,
        governance,
      ] = await Promise.all([
        api.getPortfolioPositions(),
        api.getPortfolioPerformance(),
        api.getPortfolioCashFlows(),
        api.getPortfolioRisk(),
        api.getPortfolioFxHedges(),
        api.getPortfolioBenchmarks(),
        api.getPortfolioGovernance(),
      ]);

      setData({
        positions: positions || PORTFOLIO_POSITIONS,
        performance: performance || PORTFOLIO_PERFORMANCE,
        benchmarks: benchmarks || BENCHMARK_RETURNS,
        fxHedges: fxHedges || FX_HEDGES,
        cashFlows: cashFlows || CASH_FLOW_SCHEDULE,
        riskLimits: risk?.riskLimits || RISK_LIMITS,
        stressScenarios: risk?.stressScenarios || STRESS_SCENARIOS,
        governance: governance || {
          ipsCompliance: IPS_COMPLIANCE,
          mandateLimits: MANDATE_LIMITS,
          committeeSchedule: COMMITTEE_SCHEDULE,
          auditTrail: AUDIT_TRAIL,
          regulatoryReports: REGULATORY_REPORTS,
          signatoryAuthority: SIGNATORY_AUTHORITY,
        },
      });
    } catch (err) {
      console.warn('usePortfolioData: API unavailable, using mock data fallback', err.message);
      setError(err.message);
      // Data remains at default mock values — no need to reset
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    loading,
    error,
    refresh: fetchData,
    ...data,
  };
};

export default usePortfolioData;
